import { useLanguage } from "../context/LanguageContext";
import PolicyPage, { type PolicyContent } from "./PolicyPage";
import { CONTACT_BIN, CONTACT_EMAIL, CONTACT_LEGAL_ADDRESS } from "../data/contact";

const CONTENT: Record<"ru" | "kz", PolicyContent> = {
  ru: {
    title: "Политика конфиденциальности",
    updated: "Дата последнего обновления: 31 августа 2026 г.",
    intro: [
      `Настоящая Политика конфиденциальности описывает, как ТОО «БАРКРАФТ» (BARCRAFT LLP), БИН: ${CONTACT_BIN} (далее — «Компания», «мы»), собирает, использует и защищает информацию, которую вы предоставляете при использовании сайта www.barcraft.pro.`,
    ],
    sections: [
      {
        heading: "1. Сбор информации",
        paragraphs: [
          "Мы собираем только ту информацию, которая необходима для осуществления профессиональной деятельности в сегменте B2B и выполнения условий договора поставки:",
        ],
        list: [
          "Данные компании: наименование, БИН/ИИН, юридический и фактический адрес.",
          "Контактные данные: имя представителя, адрес электронной почты, номер телефона.",
          "Информация о заказе: перечень оборудования, адрес доставки и условия оплаты.",
          "При регистрации личного кабинета на сайте — email, имя, телефон, название компании.",
          "При запросе на восстановление пароля — email.",
        ],
      },
      {
        heading: "2. Цели использования данных",
        paragraphs: ["Ваша информация используется исключительно в следующих целях:"],
        list: [
          "Оформление Счетов на оплату, Договоров и Спецификаций (включая расчет НДС 16%).",
          "Организация логистики и доставки оборудования до вашего объекта.",
          "Предоставление технической поддержки и гарантийного обслуживания.",
          "Связь с вами по вопросам статуса заказа.",
          "Создание и работа личного кабинета, обработка запросов на коммерческое предложение.",
          "Обеспечение безопасности сессии входа в личный кабинет.",
        ],
      },
      {
        heading: "3. Автоматически собираемые данные и файлы cookie",
        paragraphs: [
          "Наш сайт может использовать файлы cookie для улучшения пользовательского опыта, анализа трафика и корректной работы интерфейса, а также сохранять информацию о вашей активности в каталоге (просмотренные категории, поисковые запросы, товары, добавленные в «Проект»), чтобы каталог показывал в первую очередь товары, которые могут быть вам интересны.",
        ],
        table: {
          headers: ["Cookie", "Назначение", "Тип", "Срок хранения"],
          rows: [
            ["barcraft_session_token", "Хранит вашу сессию входа в личный кабинет", "Необходимый", "30 дней"],
            [
              "barcraft_anon_id",
              "Позволяет каталогу запоминать ваш интерес к категориям и товарам ещё до входа в личный кабинет",
              "Дополнительный, устанавливается только после согласия",
              "1 год",
            ],
          ],
        },
        list: [
          "Дополнительный cookie устанавливается только после того, как вы нажмёте «Принять» в баннере cookie. Вы можете изменить своё решение в любой момент через ссылку «Cookie-настройки» в подвале сайта.",
          "Вы также можете настроить свой браузер так, чтобы он блокировал файлы cookie, однако это может ограничить функциональность некоторых разделов сайта.",
        ],
      },
      {
        heading: "4. Защита и хранение данных",
        paragraphs: [
          "Компания принимает все необходимые административные и технические меры для защиты ваших персональных и корпоративных данных от несанкционированного доступа, изменения или раскрытия. Мы используем безопасные протоколы передачи данных и ограничиваем доступ к информации только кругом лиц, непосредственно занятых в обработке заказа.",
          "Данные учётной записи хранятся, пока ваш аккаунт активен. Данные, связанные с файлом cookie barcraft_anon_id, хранятся не дольше срока действия самого cookie (1 год) либо до его удаления через «Cookie-настройки».",
        ],
      },
      {
        heading: "5. Передача информации третьим лицам",
        paragraphs: [
          "Мы не продаём и не передаём ваши данные сторонним организациям в маркетинговых целях. Передача информации возможна только:",
        ],
        list: [
          "Логистическим и транспортным компаниям — исключительно в объеме, необходимом для доставки товара.",
          "Государственным органам — в случаях, прямо предусмотренных законодательством Республики Казахстан.",
          "Resend — сервису отправки писем для восстановления пароля.",
          "Anthropic API — тексту поискового запроса в каталоге для обработки и ранжирования результатов поиска.",
        ],
      },
      {
        heading: "6. Права пользователя",
        paragraphs: [
          "Вы имеете право в любой момент запросить информацию о том, какие ваши данные хранятся в нашей системе, а также потребовать их изменения или удаления (если это не противоречит требованиям налогового законодательства по хранению финансовой отчетности).",
        ],
        list: [
          "Изменить своё согласие на использование дополнительного cookie через ссылку «Cookie-настройки» в подвале сайта.",
          `Запросить доступ к своим данным, их исправление или удаление, написав на ${CONTACT_EMAIL}. Мы обрабатываем такие запросы вручную и отвечаем в разумный срок.`,
        ],
      },
      {
        heading: "7. Данные несовершеннолетних",
        paragraphs: [
          "Сайт не предназначен для лиц младше 18 лет, и мы сознательно не собираем данные несовершеннолетних.",
        ],
      },
      {
        heading: "8. Изменения в политике",
        paragraphs: [
          "Мы можем время от времени обновлять эту Политику. Дата последнего обновления указана в начале документа. Актуальная версия всегда доступна по адресу /privacy-policy.",
        ],
      },
      {
        heading: "9. Контактная информация",
        paragraphs: [
          "По всем вопросам, связанным с политикой конфиденциальности и защитой ваших данных, пожалуйста, обращайтесь:",
        ],
        list: [`Email: ${CONTACT_EMAIL}`, `Адрес: ${CONTACT_LEGAL_ADDRESS}`],
      },
    ],
  },
  kz: {
    title: "Құпиялылық саясаты",
    updated: "Соңғы жаңарту күні: 2026 жылғы 31 тамыз",
    intro: [
      `Осы Құпиялылық саясаты «БАРКРАФТ» ЖШС (BARCRAFT LLP), БСН: ${CONTACT_BIN} (бұдан әрі — «Компания», «біз») www.barcraft.pro сайтын пайдалану кезінде сіз беретін ақпаратты қалай жинайтынын, пайдаланатынын және қорғайтынын сипаттайды.`,
    ],
    sections: [
      {
        heading: "1. Ақпаратты жинау",
        paragraphs: [
          "Біз тек B2B сегментіндегі кәсіби қызметті жүзеге асыру және жеткізу шартының талаптарын орындау үшін қажетті ақпаратты ғана жинаймыз:",
        ],
        list: [
          "Компания деректері: атауы, БСН/ЖСН, заңды және нақты мекенжайы.",
          "Байланыс деректері: өкілдің аты-жөні, электрондық пошта мекенжайы, телефон нөмірі.",
          "Тапсырыс туралы ақпарат: жабдық тізімі, жеткізу мекенжайы және төлем шарттары.",
          "Сайттағы жеке кабинетте тіркелу кезінде — email, аты-жөні, телефон, компания атауы.",
          "Құпия сөзді қалпына келтіру сұрауы кезінде — email.",
        ],
      },
      {
        heading: "2. Деректерді пайдалану мақсаттары",
        paragraphs: ["Сіздің ақпаратыңыз тек келесі мақсаттарда пайдаланылады:"],
        list: [
          "Төлем шоттарын, Шарттарды және Спецификацияларды рәсімдеу (16% ҚҚС есептеуді қоса алғанда).",
          "Жабдықты сіздің нысаныңызға дейін жеткізу логистикасын ұйымдастыру.",
          "Техникалық қолдау және кепілдіктік қызмет көрсету.",
          "Тапсырыс мәртебесі бойынша сізбен байланысу.",
          "Жеке кабинетті құру және жұмыс істеуі, коммерциялық ұсыныс сұрауларын өңдеу.",
          "Жеке кабинетке кіру сеансының қауіпсіздігін қамтамасыз ету.",
        ],
      },
      {
        heading: "3. Автоматты түрде жиналатын деректер және cookie файлдары",
        paragraphs: [
          "Біздің сайт пайдаланушы тәжірибесін жақсарту, трафикті талдау және интерфейстің дұрыс жұмыс істеуі үшін cookie файлдарын пайдалана алады, сондай-ақ каталогтың сізге қызықты болуы мүмкін тауарларды бірінші кезекте көрсетуі үшін каталогтағы белсенділігіңіз туралы ақпаратты (қаралған санаттар, іздеу сұраулары, «Жобаға» қосылған тауарлар) сақтайды.",
        ],
        table: {
          headers: ["Cookie", "Мақсаты", "Түрі", "Сақтау мерзімі"],
          rows: [
            ["barcraft_session_token", "Жеке кабинетке кіру сеансын сақтайды", "Қажетті", "30 күн"],
            [
              "barcraft_anon_id",
              "Каталогтың жеке кабинетке кірместен бұрын да санаттар мен тауарларға деген қызығушылығыңызды есте сақтауына мүмкіндік береді",
              "Қосымша, тек келісіміңізден кейін орнатылады",
              "1 жыл",
            ],
          ],
        },
        list: [
          "Қосымша cookie тек cookie баннерінде «Қабылдау» түймесін басқаннан кейін ғана орнатылады. Шешіміңізді сайттың төменгі жағындағы «Cookie параметрлері» сілтемесі арқылы кез келген уақытта өзгерте аласыз.",
          "Сондай-ақ браузеріңізді cookie файлдарын бұғаттайтындай етіп баптай аласыз, бірақ бұл сайттың кейбір бөлімдерінің функционалдығын шектеуі мүмкін.",
        ],
      },
      {
        heading: "4. Деректерді қорғау және сақтау",
        paragraphs: [
          "Компания сіздің дербес және корпоративтік деректеріңізді рұқсатсыз қол жеткізуден, өзгертуден немесе жариялаудан қорғау үшін барлық қажетті әкімшілік және техникалық шараларды қабылдайды. Біз деректерді берудің қауіпсіз хаттамаларын қолданамыз және ақпаратқа қолжетімділікті тапсырысты өңдеумен тікелей айналысатын тұлғалармен ғана шектейміз.",
          "Есептік жазба деректері аккаунтыңыз белсенді болған уақытта сақталады. barcraft_anon_id cookie файлымен байланысты деректер cookie файлының өзінің қолданылу мерзімінен (1 жыл) артық сақталмайды немесе «Cookie параметрлері» арқылы жойылғанға дейін сақталады.",
        ],
      },
      {
        heading: "5. Ақпаратты үшінші тұлғаларға беру",
        paragraphs: [
          "Біз сіздің деректеріңізді маркетингтік мақсатта сатпаймыз және үшінші ұйымдарға бермейміз. Ақпаратты беру тек келесі жағдайларда мүмкін:",
        ],
        list: [
          "Логистикалық және көлік компанияларына — тек тауарды жеткізу үшін қажетті көлемде.",
          "Мемлекеттік органдарға — Қазақстан Республикасының заңнамасында тікелей көзделген жағдайларда.",
          "Resend — құпия сөзді қалпына келтіру хаттарын жіберу қызметіне.",
          "Anthropic API — каталогтағы іздеу сұрауының мәтінін өңдеу және нәтижелерді сұрыптау үшін.",
        ],
      },
      {
        heading: "6. Пайдаланушы құқықтары",
        paragraphs: [
          "Сіз кез келген уақытта жүйемізде қандай деректеріңіз сақталғаны туралы ақпарат сұрауға, сондай-ақ оларды өзгертуді немесе жоюды талап етуге құқылысыз (бұл қаржылық есептілікті сақтау бойынша салық заңнамасының талаптарына қайшы келмесе).",
        ],
        list: [
          "Сайттың төменгі жағындағы «Cookie параметрлері» сілтемесі арқылы қосымша cookie файлын пайдалануға келісіміңізді өзгерту.",
          `${CONTACT_EMAIL} мекенжайына жазып, өз деректеріңізге қол жеткізуді, оларды түзетуді немесе жоюды сұрау. Біз мұндай сұрауларды қолмен өңдейміз және орынды мерзімде жауап береміз.`,
        ],
      },
      {
        heading: "7. Кәмелетке толмағандардың деректері",
        paragraphs: [
          "Сайт 18 жасқа толмаған тұлғаларға арналмаған, біз кәмелетке толмағандардың деректерін әдейі жинамаймыз.",
        ],
      },
      {
        heading: "8. Саясатқа енгізілетін өзгерістер",
        paragraphs: [
          "Біз бұл Саясатты уақыт өте келе жаңартып отыруымыз мүмкін. Соңғы жаңарту күні құжаттың басында көрсетілген. Саясаттың өзекті нұсқасы әрқашан /privacy-policy мекенжайында қолжетімді.",
        ],
      },
      {
        heading: "9. Байланыс ақпараты",
        paragraphs: ["Құпиялылық саясатына және деректеріңізді қорғауға қатысты барлық сұрақтар бойынша хабарласыңыз:"],
        list: [`Email: ${CONTACT_EMAIL}`, `Мекенжай: ${CONTACT_LEGAL_ADDRESS}`],
      },
    ],
  },
};

export default function PrivacyPolicyPage() {
  const { language } = useLanguage();
  return <PolicyPage content={CONTENT[language]} />;
}
